import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
// Import API service
import { getRoomById, updateRoomPreferences } from '../services/apiService';
// Import preference mapper
import { mapPreferenceToTeacherApiFormat } from '../utils/preferenceMappers';
// Import constants
import { TEACHER_PREFERENCE_STATUS } from '../constants/preferences';
import Card from './ui/Card';
import Button from './ui/Button';
import Input from './ui/Input';
import Modal from './ui/Modal';
import TeacherPreferenceGrid from './TeacherPreferenceGrid';
import { parseSlotKey, formatTimeFromMinutes, timeToMinutes } from '../utils/helpers.ts';

// Icons
const TrashIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
    </svg>
);
const PencilIcon = () => (
    <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
        <path strokeLinecap="round" strokeLinejoin="round" d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z" />
    </svg>
);

const normalizeRoomData = (room) => ({
    ...room,
    preferences: room.preferences || [],
    maxCapacity: room.max_capacity || room.maxCapacity || '',
    isHandicapAccessible: room.is_handicap_accessible ?? room.isHandicapAccessible ?? false,
    publicVisible: room.public_visible ?? room.publicVisible ?? true,
    equipment: room.equipment || '',
    notes: room.notes || ''
});

function RoomDetailPage({ user, onUpdateRoom, onDeleteRoom }) {
    const { roomId } = useParams();
    const navigate = useNavigate();

    const [roomData, setRoomData] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const [isEditModalOpen, setIsEditModalOpen] = useState(false);
    const [editedRoom, setEditedRoom] = useState({
        name: '',
        maxCapacity: '',
        isHandicapAccessible: false,
        equipment: '',
        notes: ''
    });
    const [isUpdatingVisibility, setIsUpdatingVisibility] = useState(false);

    // Data Loading Effect
    useEffect(() => {
        const loadRoomData = async () => {
            if (!roomId) return;

            setIsLoading(true);
            setError(null);
            try {
                // Get room data including preferences
                const room = await getRoomById(roomId);
                console.log('[RoomDetailPage] Raw room data from API:', room);
                console.log('[RoomDetailPage] Raw preferences from API:', room.preferences);

                // Set room data with defaults for new fields, mapping from snake_case to camelCase
                const roomWithDefaults = normalizeRoomData(room);

                console.log('[RoomDetailPage] Room data with defaults:', roomWithDefaults);
                console.log('[RoomDetailPage] Final preferences to display:', roomWithDefaults.preferences);

                setRoomData(roomWithDefaults);
                setEditedRoom({
                    name: roomWithDefaults.name,
                    maxCapacity: roomWithDefaults.maxCapacity,
                    isHandicapAccessible: roomWithDefaults.isHandicapAccessible,
                    equipment: roomWithDefaults.equipment,
                    notes: roomWithDefaults.notes
                });
            } catch (err) {
                console.error("Error loading room:", err);
                setError(err instanceof Error ? err.message : "Failed to load room data");
                setRoomData(null);
            } finally {
                setIsLoading(false);
            }
        };

        loadRoomData();
    }, [roomId]);

    // Edit Handlers
    const openEditModal = () => {
        if (roomData) {
            setEditedRoom({
                name: roomData.name,
                maxCapacity: roomData.maxCapacity || '',
                isHandicapAccessible: roomData.isHandicapAccessible || false,
                equipment: roomData.equipment || '',
                notes: roomData.notes || ''
            });
            setIsEditModalOpen(true);
        }
    };

    const closeEditModal = () => setIsEditModalOpen(false);

    const handleEditInputChange = (e) => {
        const { name, value, type, checked } = e.target;
        setEditedRoom(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));
    };

    const handleSaveRoomDetails = async (e) => {
        e.preventDefault();
        if (!roomData) return;

        if (!editedRoom.name.trim()) {
            alert("Room name cannot be empty.");
            return;
        }

        try {
            console.log('[RoomDetailPage] Attempting to save room details:', {
                ...roomData,
                name: editedRoom.name.trim(),
                maxCapacity: editedRoom.maxCapacity.trim() || null,
                isHandicapAccessible: editedRoom.isHandicapAccessible,
                publicVisible: roomData.publicVisible !== false,
                equipment: editedRoom.equipment.trim(),
                notes: editedRoom.notes.trim()
            });

            const updatedRoom = await onUpdateRoom({
                ...roomData,
                name: editedRoom.name.trim(),
                maxCapacity: editedRoom.maxCapacity.trim() || null,
                isHandicapAccessible: editedRoom.isHandicapAccessible,
                publicVisible: roomData.publicVisible !== false,
                equipment: editedRoom.equipment.trim(),
                notes: editedRoom.notes.trim()
            });
            setRoomData(normalizeRoomData(updatedRoom));
            closeEditModal();
        } catch (err) {
            console.error("Error updating room:", err);
            console.error("Error details:", {
                message: err.message,
                status: err.status,
                response: err.response,
                stack: err.stack
            });
            alert(`Failed to update room details: ${err.message || 'Unknown error'}. Please try again.`);
        }
    };

    // Preference Update Handlers
    const updateLocalPreferences = useCallback((newPreferences) => {
        console.log('[RoomDetailPage] updateLocalPreferences called with:', newPreferences);
        setRoomData(prev => prev ? {
            ...prev,
            preferences: newPreferences
        } : null);
    }, []);

    // Save/Delete/Back Handlers
    const handleSaveChanges = async () => {
        if (!roomData) return;
        try {
            console.log('[RoomDetailPage] Saving changes for room:', roomData.id);
            console.log('[RoomDetailPage] Room preferences BEFORE transformation:', roomData.preferences);

            // First save the room preferences
            if (roomData.preferences && roomData.preferences.length > 0) {
                console.log('[RoomDetailPage] Calling updateRoomPreferences...');
                const transformedPreferences = roomData.preferences.map(pref => {
                    const transformed = mapPreferenceToTeacherApiFormat(pref);
                    console.log('[RoomDetailPage] Preference transformation:', {
                        original: pref,
                        transformed: transformed
                    });
                    return transformed;
                });
                console.log('[RoomDetailPage] ALL Transformed preferences:', transformedPreferences);
                await updateRoomPreferences(roomData.id, transformedPreferences);
                console.log('[RoomDetailPage] updateRoomPreferences completed successfully');
            } else {
                console.log('[RoomDetailPage] No preferences to save');
            }

            // Note: Room metadata is saved through the edit modal
            // If we wanted to save all data here, we could also call onUpdateRoom

            navigate('/setup/rooms');
        } catch (err) {
            console.error("Error saving room:", err);
            console.error("Error details:", {
                message: err.message,
                status: err.status,
                response: err.response,
                stack: err.stack
            });
            alert(`Failed to save changes: ${err.message || 'Unknown error'}. Please try again.`);
        }
    };

    const handleDeleteClick = async () => {
        if (!roomData) return;
        if (window.confirm(`Are you sure you want to delete room "${roomData.name}"? This action cannot be undone.`)) {
            try {
                await onDeleteRoom(roomData.id);
                navigate('/setup/rooms');
            } catch (err) {
                console.error("Error deleting room:", err);
                alert("Failed to delete room. Please try again.");
            }
        }
    };

    const handlePublicVisibilityToggle = async () => {
        if (!roomData || isUpdatingVisibility) return;

        const nextPublicVisible = roomData.publicVisible === false;
        const confirmationMessage = nextPublicVisible
            ? `Make "${roomData.name}" visible to the public?\n\nIt will appear again to the teachers and students. This change is saved immediately.`
            : `Hide "${roomData.name}" from the public?\n\nIt will no longer appear to the teachers and students. It will still be visible to the admin.`;

        if (!window.confirm(confirmationMessage)) {
            return;
        }

        setIsUpdatingVisibility(true);
        try {
            const updatedRoom = await onUpdateRoom({
                id: roomData.id,
                name: roomData.name,
                publicVisible: nextPublicVisible
            });
            setRoomData(normalizeRoomData(updatedRoom));
        } catch (err) {
            console.error("Error updating room public visibility:", err);
            alert(`Failed to update room visibility: ${err.message || 'Unknown error'}. Please try again.`);
        } finally {
            setIsUpdatingVisibility(false);
        }
    };

    const handleGoBack = () => navigate('/setup/rooms');

    // --- Render Logic ---
    if (isLoading) return <div className="p-6 text-center">Loading room details...</div>;
    if (error) return (
        <div className="p-6 text-center text-red-600">
            <p>{error}</p>
            <Button variant="secondary" onClick={handleGoBack} className="mt-4">Back to Rooms List</Button>
        </div>
    );
    if (!roomData) return (
        <div className="p-6 text-center text-gray-500">
            <p>Room data could not be loaded.</p>
            <Button variant="secondary" onClick={handleGoBack} className="mt-4">Back to Rooms List</Button>
        </div>
    );

    return (
        <div className="space-y-6">
            {/* Top Bar */}
            <div className="relative flex justify-between items-center mb-4 min-h-[40px]">
                <Button variant="secondary" onClick={handleGoBack}>
                    &larr; Back to Rooms List
                </Button>
                <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 flex items-center gap-2">
                    <h2 className="text-2xl font-bold text-gray-800 text-center whitespace-nowrap">
                        Room: {roomData.name}
                        {roomData.room_code && <span className="text-xl font-normal text-gray-500 ml-1">({roomData.room_code})</span>}
                    </h2>
                    <Button
                        variant="secondary"
                        onClick={openEditModal}
                        className="p-1 text-gray-500 hover:text-gray-700 flex-shrink-0"
                        title="Edit room details"
                    >
                        <PencilIcon />
                    </Button>
                </div>
                <div className="flex items-center justify-end">
                    <button
                        type="button"
                        role="switch"
                        aria-checked={roomData.publicVisible !== false}
                        onClick={handlePublicVisibilityToggle}
                        disabled={isUpdatingVisibility}
                        className={`inline-flex items-center gap-2 rounded-full border px-3 py-1.5 text-sm font-medium transition-colors ${roomData.publicVisible !== false
                            ? 'border-green-200 bg-green-50 text-green-700'
                            : 'border-gray-200 bg-gray-100 text-gray-600'
                            } ${isUpdatingVisibility ? 'opacity-60 cursor-not-allowed' : 'hover:bg-opacity-80'}`}
                        title="Toggle public room visibility"
                    >
                        <span
                            className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${roomData.publicVisible !== false ? 'bg-green-500' : 'bg-gray-400'
                                }`}
                        >
                            <span
                                className={`inline-block h-4 w-4 transform rounded-full bg-white shadow transition-transform ${roomData.publicVisible !== false ? 'translate-x-4' : 'translate-x-1'
                                    }`}
                            />
                        </span>
                        <span>{roomData.publicVisible !== false ? 'Public' : 'Hidden'}</span>
                    </button>
                </div>
            </div>

            {/* Main Content Grid */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                {/* Left Column - Room Details */}
                <div className="space-y-6">
                    {/* Room Information Card */}
                    <Card>
                        <div className="p-4">
                            <div className="flex justify-between items-center mb-4 border-b pb-2">
                                <h3 className="text-lg font-semibold text-gray-700">Room Information</h3>
                                <Button
                                    variant="secondary"
                                    onClick={openEditModal}
                                    className="p-1 text-gray-500 hover:text-gray-700 flex-shrink-0"
                                    title="Edit room details"
                                >
                                    <PencilIcon />
                                </Button>
                            </div>
                            <div className="space-y-3">
                                <div>
                                    <span className="text-sm font-medium text-gray-700">Name:</span>
                                    <span className="ml-2 text-sm text-gray-900">{roomData.name}</span>
                                </div>
                                {roomData.room_code && (
                                    <div>
                                        <span className="text-sm font-medium text-gray-700">Room Code:</span>
                                        <span className="ml-2 text-sm text-gray-900">{roomData.room_code}</span>
                                    </div>
                                )}
                                <div>
                                    <span className="text-sm font-medium text-gray-700">Public Visibility:</span>
                                    <span className="ml-2 text-sm text-gray-900">{roomData.publicVisible !== false ? 'Visible' : 'Hidden'}</span>
                                </div>
                                <div>
                                    <span className="text-sm font-medium text-gray-700">Max Capacity:</span>
                                    <span className="ml-2 text-sm text-gray-900">{roomData.maxCapacity || 'Not specified'}</span>
                                </div>
                                <div>
                                    <span className="text-sm font-medium text-gray-700">Handicap Accessible:</span>
                                    <span className="ml-2 text-sm text-gray-900">{roomData.isHandicapAccessible ? 'Yes' : 'No'}</span>
                                </div>
                                {roomData.equipment && (
                                    <div>
                                        <span className="text-sm font-medium text-gray-700">Equipment:</span>
                                        <p className="mt-1 text-sm text-gray-900">{roomData.equipment}</p>
                                    </div>
                                )}
                                {roomData.notes && (
                                    <div>
                                        <span className="text-sm font-medium text-gray-700">Notes:</span>
                                        <p className="mt-1 text-sm text-gray-900">{roomData.notes}</p>
                                    </div>
                                )}
                            </div>
                        </div>
                    </Card>

                    {/* Actions Card */}
                    <Card>
                        <div className="p-4">
                            <h3 className="text-lg font-semibold text-gray-700 mb-4 border-b pb-2">Actions</h3>
                            <div className="flex flex-col space-y-3">
                                <Button variant="primary" onClick={handleSaveChanges}>
                                    Save Changes
                                </Button>
                                <Button
                                    variant="danger"
                                    onClick={handleDeleteClick}
                                    className="flex items-center justify-center space-x-2"
                                >
                                    <TrashIcon />
                                    <span>Delete Room</span>
                                </Button>
                            </div>
                        </div>
                    </Card>
                </div>

                {/* Right Column - Time Preferences */}
                <div className="space-y-6">
                    <Card>
                        <div className="p-4">
                            <h3 className="text-lg font-semibold text-gray-700 mb-2 border-b pb-2">Time Preferences</h3>
                            <p className="text-sm text-gray-600 mb-1">Click or drag grid cells to edit room availability preferences.</p>
                            <div className="flex items-center flex-wrap gap-x-4 gap-y-1 mb-3 text-xs text-gray-600">
                                <span className="font-semibold">Legend:</span>
                                <div className="flex items-center"><span className="w-3 h-3 rounded-sm bg-green-200 mr-1 inline-block border border-gray-300"></span><span>Available</span></div>
                                <div className="flex items-center"><span className="w-3 h-3 rounded-sm bg-orange-200 mr-1 inline-block border border-gray-300"></span><span>Prefer Not</span></div>
                                <div className="flex items-center"><span className="w-3 h-3 rounded-sm bg-red-200 mr-1 inline-block border border-gray-300"></span><span>Unavailable</span></div>
                            </div>
                            <TeacherPreferenceGrid
                                preferences={roomData.preferences || []}
                                onPreferencesChange={updateLocalPreferences}
                            />
                        </div>
                    </Card>
                </div>
            </div>

            {/* Edit Room Modal */}
            <Modal isOpen={isEditModalOpen} onClose={closeEditModal} title="Edit Room Details">
                <form onSubmit={handleSaveRoomDetails}>
                    <div className="space-y-4">
                        <div>
                            <label htmlFor="edit-room-name" className="block text-sm font-medium text-gray-700 mb-1">Room Name *</label>
                            <Input
                                id="edit-room-name"
                                name="name"
                                value={editedRoom.name}
                                onChange={handleEditInputChange}
                                required
                                autoFocus
                            />
                        </div>

                        <div>
                            <label htmlFor="edit-room-capacity" className="block text-sm font-medium text-gray-700 mb-1">Max Capacity</label>
                            <Input
                                id="edit-room-capacity"
                                name="maxCapacity"
                                type="number"
                                value={editedRoom.maxCapacity}
                                onChange={handleEditInputChange}
                                placeholder="e.g., 30"
                                min="1"
                            />
                        </div>

                        <div className="flex items-center">
                            <input
                                id="edit-room-handicap"
                                name="isHandicapAccessible"
                                type="checkbox"
                                checked={editedRoom.isHandicapAccessible}
                                onChange={handleEditInputChange}
                                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                            />
                            <label htmlFor="edit-room-handicap" className="ml-2 block text-sm text-gray-700">
                                Handicap Accessible
                            </label>
                        </div>

                        <div>
                            <label htmlFor="edit-room-equipment" className="block text-sm font-medium text-gray-700 mb-1">Equipment</label>
                            <textarea
                                id="edit-room-equipment"
                                name="equipment"
                                value={editedRoom.equipment}
                                onChange={handleEditInputChange}
                                rows={3}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-vertical"
                                placeholder="e.g., Projector, Whiteboard, Audio system..."
                            />
                        </div>

                        <div>
                            <label htmlFor="edit-room-notes" className="block text-sm font-medium text-gray-700 mb-1">Notes</label>
                            <textarea
                                id="edit-room-notes"
                                name="notes"
                                value={editedRoom.notes}
                                onChange={handleEditInputChange}
                                rows={3}
                                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent resize-vertical"
                                placeholder="Additional notes about the room..."
                            />
                        </div>
                    </div>

                    <div className="mt-6 flex justify-end space-x-3">
                        <Button type="button" variant="secondary" onClick={closeEditModal}>Cancel</Button>
                        <Button type="submit" variant="primary">Save Changes</Button>
                    </div>
                </form>
            </Modal>
        </div>
    );
}

export default RoomDetailPage; 